import React, { lazy, Suspense } from 'react';
import { Route, Switch, Redirect as RedirectRoute } from 'react-router-dom';
import type { RouterHistory, Location, Redirect } from 'react-router-dom';
import i18next from 'i18next';
import moment from 'moment';
import queryString from 'query-string';

import requiresAuth from 'utils/hoc/requiresAuth';
import requiresPlan from 'utils/hoc/requiresPlan';
import withBaseElements from 'utils/hoc/withBaseElements';
import { useUser, useToken, useUpdateUser } from 'utils/contexts/app-context';
import ChangePasswordDialog from 'components/ChangePasswordDialog';
import MfaMethodSelectionDialog from 'containers/profile/mfa-method-selection-dialog';
import SmsSetupDialog from 'containers/profile/mfa-sms-setup-dialog';
import TotpSetupDialog from 'containers/profile/mfa-totp-setup-dialog';
import axios from 'axios';
import { AUTH, PUBLIC, PRIVATE, ADMIN } from './routes';

const Login = lazy(() => import('containers/login'));
const Register = lazy(() => import('containers/register'));
const Activate = lazy(() => import('containers/activate'));
const ResetPassword = lazy(() => import('containers/reset-password'));
const MagicLinkVerify = lazy(() => import('containers/login/MagicLinkVerify/MagicLinkVerify'));
const MfaVerify = lazy(() => import('containers/mfa-verify'));
// const UpdatePassword = lazy(() => import('containers/update-password'));


const Profile = lazy(() => import('containers/profile'));
const Report = lazy(() => import('containers/report-page/index.tsx'));
// const LEVReport = lazy(() => import('containers/lev/Report'))

const Company = lazy(() => import('containers/company'));


const Terms = lazy(() => import('containers/terms'));
const Privacy = lazy(() => import('containers/privacy'));


// NEW PAGES
const ValidationsNew = lazy(() => import('../pages/validations'))
const TeamDetails = lazy(() => import('../pages/teams/TeamDetails'))
const Teams = lazy(() => import('../pages/teams'))
const Users = lazy(() => import('../pages/users'))

const IvReport  = lazy(() => import('../pages/individualReport'))
const LEVReport = lazy(() => import('../pages/levReport'))



const switchLanguage = (location: Location, history: RouterHistory) => {
  if (!location) return;

  const params = queryString.parse(location.search);
  if (
    params.language &&
    i18next.language &&
    !i18next.language.includes(params.language)
  ) {
    i18next.changeLanguage(params.language);
    moment.locale(params.language);
    if (location.pathname === '/activate') {
      history.replace(location.pathname + location.search);
    }
    else {
      history.replace(location.pathname + location.search)
    }
  }
};

const AuthRouter = ({ history, location }) => {
  switchLanguage(location, history);

  return (
    <Suspense fallback={<div>Loading...</div>}>
      <Switch>
        <Route exact path={AUTH.magic_link} component={MagicLinkVerify} />
        <Route exact path={AUTH.mfaVerify} component={MfaVerify} />
        <Route exact path={AUTH.register} component={Register} />
        <Route exact path={AUTH.login} component={Login} />
        <Route exact path={AUTH.activate} component={Activate} />
        <Route exact path={AUTH.reset_password} component={ResetPassword} />
        {/* <Route path={AUTH.update_password} component={UpdatePassword} /> */}

        <RedirectRoute to={AUTH.login} />
      </Switch>
    </Suspense>
  );
};

const PrivateRouter = withBaseElements(({ history, location }: Props) => {
  switchLanguage(location, history);
  const user = useUser();
  const token = useToken();
  const updateUser = useUpdateUser();
  const [showChangePasswordDialog, setShowChangePasswordDialog] = React.useState(false);
  const [mfaSetupRequired, setMfaSetupRequired] = React.useState(false);
  const [mfaSetupDialogOpen, setMfaSetupDialogOpen] = React.useState(false);
  const [smsSetupDialogOpen, setSmsSetupDialogOpen] = React.useState(false);
  const [totpSetupDialogOpen, setTotpSetupDialogOpen] = React.useState(false);

  // Check if password was set by admin and show change password dialog
  React.useEffect(() => {
    if (user && user.passwordSetByAdmin && user?.company?.authenticate_mode === 'Default') {
      setShowChangePasswordDialog(true);
    }
  }, [user]);

  // Be defensive here: sometimes `location` can be undefined on first render,
  // and the pathname may include extra segments. Treat anything under
  // `/app/profile` as "on profile page".
  const currentPath =
    (location && location.pathname) ||
    (typeof window !== 'undefined' ? window.location.pathname : '');
  const isOnProfilePage =
    typeof currentPath === 'string' &&
    (currentPath.indexOf(PRIVATE.profile) === 0 || 
     currentPath === PRIVATE.profile || 
     currentPath === PRIVATE.profileSecurity ||
     currentPath === PRIVATE.profileValidations ||
     currentPath.includes('/profile'));

  // Check if user is admin or owner (can disable company MFA)
  const isAdmin = user?.privilegeLevel === 'ADMIN' || user?.accessLevel === 'ADMIN';
  const isOwner = user?.isOwner || user?.privilegeLevel === 'OWNER' || user?.accessLevel === 'OWNER';
  const canDisableCompanyMfa = isAdmin || isOwner;

  // Check if MFA setup is required (from /me response)
  React.useEffect(() => {
    if (user && user.needsMfaSetup && !isOnProfilePage) {
      setMfaSetupRequired(true);
      setMfaSetupDialogOpen(true);
    } else {
      setMfaSetupRequired(false);
      setMfaSetupDialogOpen(false);
    }
  }, [user, isOnProfilePage]);

  return (
    <Suspense fallback={<div>Loading...</div>}>
      <Switch>
        <Route exact path={PRIVATE.profile} component={Profile} />
        <Route exact path={PRIVATE.profileSecurity} component={Profile} />
        <Route exact path={PRIVATE.profileValidations} component={Profile} />
   
        <Route path={PRIVATE.validations}>
            <ValidationsNew />
        </Route>
        <Route path={`${PRIVATE.report}/:uuid`} component={IvReport} />
        <Route path={`${PRIVATE.lev_report}/:uuid`} component={LEVReport} />
        <Route path={`${PRIVATE.old_report}/:uuid`} component={IvReport} />
        <RedirectRoute to={PRIVATE.validations} />
      </Switch>

      <ChangePasswordDialog 
        open={showChangePasswordDialog} 
        onClose={() => setShowChangePasswordDialog(false)} 
      />
      
      {/* MFA Setup Dialog - Show when needsMfaSetup is true from /me response */}
      {mfaSetupRequired && (
        <>
          <MfaMethodSelectionDialog
            open={mfaSetupDialogOpen}
            onClose={async () => {
              // Only allow closing if user is admin/owner (can disable company MFA)
              // Regular users MUST set up MFA - dialog cannot be closed
              if (canDisableCompanyMfa) {
                try {
                  const SERVER_URL = `${process.env.REACT_APP_NODE_SERVER_URL || ''}/app`;
                  const response = await axios.post(
                    `${SERVER_URL}/auth/mfa/company/disable`,
                    {},
                    {
                      headers: { Authorization: `Bearer ${token}` }
                    }
                  );
                  
                  if (response.status === 200 || response.data?.success) {
                    // MFA disabled, refresh user data
                    setMfaSetupRequired(false);
                    setMfaSetupDialogOpen(false);
                    // Refresh user data to get updated MFA status
                    if (token) {
                      await updateUser(true); // useFresh = true
                    }
                  }
                } catch (err) {
                  console.error('[ROUTER] Error disabling company MFA on cancel:', err);
                  // Still close the dialog even if there's an error
                  setMfaSetupDialogOpen(false);
                }
              }
              // Regular user - do nothing, dialog stays open (they must set up MFA)
            }}
            onSelectMethod={(method) => {
              setMfaSetupDialogOpen(false);
              if (method === 'SMS') {
                setSmsSetupDialogOpen(true);
              } else if (method === 'TOTP') {
                setTotpSetupDialogOpen(true);
              }
            }}
            canDisableCompanyMfa={canDisableCompanyMfa}
            onDisableCompanyMfa={async () => {
              try {
                // Disable company MFA - user is already authenticated
                const SERVER_URL = `${process.env.REACT_APP_NODE_SERVER_URL || ''}/app`;
                const response = await axios.post(
                  `${SERVER_URL}/auth/mfa/company/disable`,
                  {},
                  {
                    headers: { Authorization: `Bearer ${token}` }
                  }
                );
                
                if (response.data.success) {
                  // MFA disabled, refresh user data
                  setMfaSetupRequired(false);
                  setMfaSetupDialogOpen(false);
                  // Refresh user data to get updated MFA status
                  if (token) {
                    await updateUser(true); // useFresh = true
                  }
                }
              } catch (err) {
                console.error('[ROUTER] Error disabling company MFA:', err);
                // Show error to user - could add error state here if needed
              }
            }}
          />
          <SmsSetupDialog
            open={smsSetupDialogOpen}
            onClose={() => {
              // Return to method selection
              setSmsSetupDialogOpen(false);
              setMfaSetupDialogOpen(true);
            }}
            onSuccess={async () => {
              // MFA setup complete - refresh user data
              setSmsSetupDialogOpen(false);
              setMfaSetupRequired(false);
              // Refresh user data to get updated MFA status
              if (token) {
                await updateUser(true); // useFresh = true
              }
            }}
          />
          <TotpSetupDialog
            open={totpSetupDialogOpen}
            onClose={() => {
              // Return to method selection
              setTotpSetupDialogOpen(false);
              setMfaSetupDialogOpen(true);
            }}
            onSuccess={async () => {
              // MFA setup complete - refresh user data
              setTotpSetupDialogOpen(false);
              setMfaSetupRequired(false);
              // Refresh user data to get updated MFA status
              if (token) {
                await updateUser(true); // useFresh = true
              }
            }}
          />
        </>
      )}
    </Suspense>
  );

});

const AdminRouter = withBaseElements(({ history, location }: Props) => {
  const user = useUser();
  switchLanguage(location, history);

// console.log('user', user)
  return (
    <Suspense fallback={<div>Loading...</div>}>
      <Switch>
        {(user.accessLevel === 'OWNER' || user.accessLevel === 'ADMIN') &&
          <Route path={ADMIN.delegates}>
            <Users />
          </Route>
        }

        {(user.accessLevel === 'OWNER' || user.accessLevel === 'ADMIN') &&
          <Route path={ADMIN.teams}>
            <Teams />
          </Route>
        }

        <Route path={ADMIN.team}>
          <TeamDetails />
        </Route>
        <Route path={ADMIN.company} component={Company} />
      </Switch>
    </Suspense>
  );
});

const PublicRouter = ({ history, location }: Props) => {
  switchLanguage(location, history);
  const websiteUrl = 'https://www.valital.com';

  return (
    <Suspense fallback={<div>Loading...</div>}>
      <Switch>
        <Route path={PUBLIC.terms_of_use} component={Terms} />
        <Route path={PUBLIC.privacy_policy} component={Privacy} />
        <Route
          path={PUBLIC.website}
          component={() => {
            window.location.href =
              i18next.language === 'fr' ? `${websiteUrl}/fr` : websiteUrl;
          }}
        />
      </Switch>
    </Suspense>
  );
};

type Props = {
  location: Location,
  history: RouterHistory,
};

export default {
  AuthRouter,
  PublicRouter,
  PrivateRouter: requiresAuth(requiresPlan(PrivateRouter)),
  AdminRouter: requiresAuth(requiresPlan(AdminRouter, false), {
    authType: 'ADMIN',
    redirectTo: PRIVATE.dashboard,
  }),
};
